import { ResponseConta } from '@/services/contas/contas.service';
import { useOnboardingAoVivoClienteStore } from '@/stores/app/onboardingAoVivoStore';
import type { ItemAoVivoCliente } from '@/services/meta-trilha/meta-onboarding-ao-vivo.service';
import { InboxService, LinkedInbox, ResponseInbox, UnconnectedInbox } from '@/services/inbox/inbox.service';
import { UsuarioAuthService } from '@/services/usuarios/usuarios-auth.service'
import { UsuariosService } from '@/services/usuarios/usuarios.service';
import { defineStore } from 'pinia'
import { ForceLogoutPayload, InboxAtualizadoPayload, InboxBotIaDesvinculadoPayload, InboxProntoPayload, UsuarioInboxVinculoAlteradoPayload, WebhookPayload, WebsocketService, WppHistorySyncPayload } from '../../services/websocket/websocket.service';
import { toast } from 'vue3-toastify';
import { useChatStore } from './chatStore';
import { IdCanal, IdConta, IdInbox, IdUsuario, makeIdConta } from '@/services/common-types';
import { useInternalChatStore } from './chatInternoStore';
import { useTemplateStore } from './templateStore';
import { useQuadroKanbanStore } from './quadroKanbanStore';
import { FetchUtil } from '../../api/fetchutil';
import { usePermissoesStore } from './permissoesStore';
import { useContatoStore } from './contatoStore';
import { useUiStore } from "./uiStore";
import { useInboxAlertStore } from './inboxAlertStore';
import { useSoundStore, SoundPreferences } from './soundStore';
import { UsuariosCanaisService } from '@/services/usuarios-canais/usuarios-canais.service';
import { suportaTemplatesMeta } from '@/utils/plataforma';
import { mensagemDeFalhaAoSimular } from '@/utils/erroSimularLogin';

export type UserAuthStore = ReturnType<typeof useAuthStoreUser>;
export const useAuthStoreUser = defineStore('authUser', {
  state: () => ({
    initialized: false,
    isLoggedIn: false as boolean,
    requires2fa: false as boolean,
    user: null as null | { id: IdUsuario, nome: string, email: string, foto?: string, hasGlobalAccess?: boolean, doisFatores?: boolean, notificacaoNavegador?: boolean },
    /** F-06: sessão aberta por "Simular login" do admin (vem do who-am-i). */
    sessaoSimulada: null as null | { nomeAdmin?: string, expiraEm?: number },
    canais: {} as Record<IdCanal, boolean>,
    canaisVisualizacaoGlobal: {} as Record<IdCanal, boolean>,
    contas: {} as Record<IdConta, boolean>,
    permissoes: {} as Record<string, boolean>,
    inboxes: {} as Record<IdInbox, ResponseInbox>,
    // Inboxes Meta criadas por admin mas ainda não conectadas — listadas no
    // InboxModal para o usuário conectar. Vêm separadas de `inboxes` (sem credencial).
    inboxesNaoConectadas: [] as UnconnectedInbox[],
    // TODOS os inboxes vinculados ao usuário na conta atual (só `del = false`),
    // tenham ou não integração configurada. É a fonte do filtro de instâncias:
    // `inboxesFull` só contém quem tem sub-registro, e por isso um inbox
    // vinculado porém sem integração sumia do filtro. Ver `LinkedInbox`.
    inboxesVinculados: [] as LinkedInbox[],
    // Último INBOX_PRONTO recebido. Existe para a UI poder aguardar o evento sem
    // registrar um listener próprio: `onInboxPronto` é registrado com
    // `isExclusiveListener`, então um segundo `registerCallback('INBOX_PRONTO')`
    // seria descartado (ou descartaria o da store) no próximo re-registro.
    // O `em` permite distinguir um evento novo de um anterior à espera atual.
    ultimoInboxPronto: null as null | { id: IdInbox, em: number },
    service: new UsuarioAuthService(),
    usuarioService: new UsuariosService(),
    usuarioCanalService: new UsuariosCanaisService(),
    contasFull: [] as ResponseConta[],
    inboxesFull: [] as ResponseInbox[],

    currentConta: null as null | ResponseConta,

    wsServiceChat: null as WebsocketService | null,
    wsServiceInternal: null as WebsocketService | null,
    wsService: null as WebsocketService | null,
  }),
  getters: {
    currentAccountInboxes(): ResponseInbox[] {
      return this.initialized ? this.inboxesFull.filter(i => i.idConta == this.currentConta?.id) : []
    },
    currentAccountHasAtLeastOneInboxWithAiAgent(): boolean {
      return this.currentAccountInboxes.some(ibx => !!ibx.idInstanciaBotIa);
    },
    /**
     * Inboxes capazes de enviar template oficial Meta. Inclui coexistência: a
     * metade Cloud API está lá, e é dela que saem os templates.
     */
    currentAccountInboxWithOfficialWppIntegration(): ResponseInbox[] {
      return this.currentAccountInboxes.filter(i => suportaTemplatesMeta(i.plataforma));
    },
    currentAccountHasOfficialWppIntegration(): boolean {
      return this.currentAccountInboxes.some(i => suportaTemplatesMeta(i.plataforma));
    },
    currentAccountHasComercialInbox(): boolean {
      return this.currentAccountInboxes.some(i => i.comercial);
    }
  },
  actions: {
    async initWebsocket() {
      const ws = WebsocketService.getWSS(
        () => this.service.getWebsocketToken(),
        window.configs.SOCKET_URL_PROD as string
      );

      ws.registerCallback<ForceLogoutPayload>(
        'FORCE_LOGOUT',
        async (payload) => {
          console.warn('🚨 Logout forçado recebido:', payload);

          if (payload.usuarioId == this.user?.id) {
            // 🛑 impede reconexão automática
            ws?.ws.off('disconnect');

            await this.logout();
            window.location.href = '/';
          }
        }, undefined, true
      );

      // Auto-onboarding: o backend cria o inbox WhatsApp e emite INBOX_PRONTO na
      // sala do usuário. Recarregamos a sessão (token novo já inclui o inbox),
      // repopulamos o chat e selecionamos o inbox — tudo automaticamente.
      ws.registerCallback<InboxProntoPayload>(
        'INBOX_PRONTO',
        async (payload) => {
          console.log('[authUser] INBOX_PRONTO recebido:', payload);
          await this.onInboxPronto(payload);
        }, undefined, true
      );

      // Onboarding Meta ao vivo: cada etapa da conexão (sala do usuário que
      // iniciou e da inbox). Alimenta a barra flutuante do painel.
      ws.registerCallback<ItemAoVivoCliente & WebhookPayload>(
        'META_ONBOARDING_PROGRESSO',
        (item) => { useOnboardingAoVivoClienteStore().aplicar(item); },
        undefined, true
      );

      // Coexistência (req 2.3): sincronização do histórico do app WhatsApp
      // Business. Os eventos chegam na sala do inbox recém-vinculado; refletimos
      // o progresso ao usuário com toasts.
      ws.registerCallback<WppHistorySyncPayload>('WPP_HISTORY_SYNC_COMPLETE', () => {
        toast.success('Sincronização concluída — seu histórico do WhatsApp já está disponível. ✅');
      }, undefined, true);
      ws.registerCallback<WppHistorySyncPayload>('WPP_HISTORY_SYNC_FAILED', () => {
        toast.warning('Não foi possível sincronizar o histórico do WhatsApp Business.');
      }, undefined, true);
      ws.registerCallback<WppHistorySyncPayload>('WPP_HISTORY_SYNC_EXPIRED', () => {
        toast.warning('A sincronização do histórico não foi concluída a tempo. Conecte o WhatsApp novamente.');
      }, undefined, true);

      // O bot de IA excluiu a instância/agente que atendia por um inbox desta conta
      // e o servidor desfez o vínculo. Avisamos porque a consequência é silenciosa:
      // as próximas mensagens daquele inbox deixam de ser atendidas pela IA.
      ws.registerCallback<InboxBotIaDesvinculadoPayload>(
        'INBOX_BOT_IA_DESVINCULADO',
        (payload) => { this.onInboxBotIaDesvinculado(payload); },
        undefined, true,
      );

      // O caminho inverso: o bot de IA vinculou ao inbox a instância/agente que acabou de
      // criar. Sem isso, o inbox cacheado aqui continuaria dizendo "sem bot" e a tela só
      // mudaria no próximo carregamento.
      ws.registerCallback<InboxAtualizadoPayload>(
        'INBOX_ATUALIZADO',
        (payload) => { this.onInboxAtualizado(payload); },
        undefined, true,
      );

      // Um admin vinculou ou desvinculou este usuário de um inbox. Sem isto, a
      // mudança só aparecia no próximo carregamento da página: o inbox novo não
      // entrava na lista, e o removido continuava lá — com as salas de socket e
      // as conversas de um inbox que o servidor já não autoriza.
      ws.registerCallback<UsuarioInboxVinculoAlteradoPayload>(
        'USUARIO_INBOX_VINCULO_ALTERADO',
        async (payload) => { await this.onVinculoInboxAlterado(payload); },
        undefined, true,
      );

      if (!ws.ws.connected) {
        await ws.connect();
      }

      // DEBUG: permite forçar o fluxo de INBOX_PRONTO pelo console quando não há
      // NATS/websocket rodando. Ex.: window.__forceInboxPronto('<idInbox>').
      (window as unknown as { __forceInboxPronto?: (id?: string) => void }).__forceInboxPronto =
        (id?: string) => { void this.debugForceInboxPronto(id as IdInbox | undefined); };

      this.wsService = ws;
      return ws;
    },

    /**
     * Trata o evento INBOX_PRONTO: renova o token (agora com o inbox), revalida a
     * sessão e permissões, popula o inboxMap do chat e seleciona o novo inbox.
     */
    /**
     * O bot de IA perdeu a instância/agente vinculado a um inbox desta conta e o
     * servidor já desfez o vínculo. Reflete o estado novo em memória (o inbox
     * cacheado ainda carrega os ids antigos) e avisa o usuário: sem o aviso, o
     * atendimento por IA simplesmente pararia, sem nada na tela mudando.
     */
    onInboxBotIaDesvinculado(payload: InboxBotIaDesvinculadoPayload) {
      // PLANO: o socket-server relaia `emit(tipo, dados)` e entrega só o `dados`.
      // Ler `payload.dados` devolvia `undefined` e este handler saía na linha
      // seguinte — o evento chegava e nada acontecia, sem erro nem log. O
      // `?? payload` aceita também o envelope inteiro, se algum produtor mandar.
      const dados = (payload as { dados?: InboxBotIaDesvinculadoPayload })?.dados ?? payload;
      if (!dados?.idInbox) return;

      const aplicar = (inbox: ResponseInbox | undefined) => {
        if (!inbox) return;
        if (dados.motivo === 'INSTANCIA_EXCLUIDA') inbox.idInstanciaBotIa = undefined;
        inbox.idAgenteBotIa = undefined;
      };
      aplicar(this.inboxes[dados.idInbox]);
      aplicar(this.inboxesFull.find(i => i.id == dados.idInbox));
      // O `inboxMap` do chatStore é uma TERCEIRA cópia do inbox, carregada uma vez
      // por `_fetchAndPopulateInboxes` e atualizada por websocket só quanto a
      // conexão (CONEXAO/DESCONEXAO/QR_CODE). Sem esta linha ela ficava com o
      // vínculo de bot de IA congelado no estado do login — e é justamente ela que
      // a modal de inbox lê e devolve ao servidor no PATCH. Ver o comentário em
      // `atualizarVinculoBotIaNoChatStore`.
      this.atualizarVinculoBotIaNoChatStore(dados.idInbox);

      const recurso = dados.motivo === 'INSTANCIA_EXCLUIDA' ? 'A instância' : 'O agente';
      const nome = dados.nomeRecurso ? ` "${dados.nomeRecurso}"` : '';
      toast.warning(
        `${recurso}${nome} de IA do inbox "${dados.nomeInbox}" foi excluído no painel de bots. ` +
        'O inbox ficou sem atendimento automático até um novo agente ser vinculado.',
        { autoClose: 10000 },
      );
    },

    /**
     * O vínculo do inbox com o bot de IA mudou por fora desta aba — hoje, porque alguém criou
     * no painel de bots uma instância/agente apontando para este inbox.
     *
     * Espelha o `onInboxBotIaDesvinculado`: atualiza os inboxes em memória (o cache local ainda
     * carrega os ids antigos) e avisa. Aqui o aviso é `info`, não `warning`: ganhar atendimento
     * automático não é um problema — mas *substituir* o bot que atendia é uma mudança de
     * comportamento que quem está no inbox precisa saber.
     */
    onInboxAtualizado(payload: InboxAtualizadoPayload) {
      // PLANO — mesma razão de `onInboxBotIaDesvinculado`, logo acima.
      const dados = (payload as { dados?: InboxAtualizadoPayload })?.dados ?? payload;
      if (!dados?.idInbox || dados.motivo !== 'VINCULO_BOT_IA') return;

      const aplicar = (inbox: ResponseInbox | undefined) => {
        if (!inbox) return;
        inbox.idInstanciaBotIa = dados.idInstanciaBotIa ?? undefined;
        inbox.idAgenteBotIa = dados.idAgenteBotIa ?? undefined;
      };
      aplicar(this.inboxes[dados.idInbox]);
      aplicar(this.inboxesFull.find(i => i.id == dados.idInbox));
      this.atualizarVinculoBotIaNoChatStore(dados.idInbox);

      const substituiu = !!dados.anterior?.idInstanciaBotIa || !!dados.anterior?.idAgenteBotIa;
      const nome = dados.nomeRecurso ? ` "${dados.nomeRecurso}"` : '';
      toast.info(
        substituiu
          ? `O bot de IA${nome} substituiu o anterior no inbox "${dados.nomeInbox}".`
          : `O inbox "${dados.nomeInbox}" passou a ser atendido pelo bot de IA${nome}.`,
        { autoClose: 8000 },
      );
    },

    /**
     * O vínculo deste usuário com um inbox mudou por fora desta aba (um admin
     * vinculou ou desvinculou).
     *
     * Revalida a sessão INTEIRA em vez de remendar a lista em memória: o vínculo
     * decide ao mesmo tempo o mapa `inboxes` do token, a lista de não-conectadas
     * e as permissões de escopo inbox — e o servidor já invalidou os caches dos
     * três antes de publicar o evento, então `init(true)` traz o retrato novo.
     *
     * No DESVÍNCULO a limpeza do chat importa mais que o aviso: sem ela o front
     * continua com as conversas e as salas de socket de um inbox que o servidor
     * já não autoriza. `chatStore.init(true)` refaz a lista a partir do token
     * novo, que não contém mais o inbox.
     */
    async onVinculoInboxAlterado(payload: UsuarioInboxVinculoAlteradoPayload) {
      // O socket-server relaia `emit(tipo, dados)`: o que chega é o conteúdo de
      // `dados`, PLANO. O `?? payload` aceita também o envelope completo, para o
      // caso de alguém publicar com ele — ler só `payload.dados` era o que fazia
      // este handler desistir em silêncio com o evento já na mão.
      const dados = (payload as { dados?: UsuarioInboxVinculoAlteradoPayload })?.dados ?? payload;
      if (!dados?.idInbox || dados.idUsuario !== this.user?.id) return;

      const nome = dados.nomeInbox ? `"${dados.nomeInbox}"` : 'um inbox';
      try {
        await this.init(true);
        await useChatStore().init(true);
      } catch (e) {
        // Falhar aqui deixaria a store num retrato intermediário; o aviso ainda
        // vale, porque diz ao usuário por que a tela pode estar inconsistente.
        console.warn('[authUser] falha ao revalidar a sessão após mudança de vínculo de inbox', e);
        toast.warning(
          `Seu acesso a ${nome} mudou, mas não foi possível atualizar a tela. Recarregue a página.`,
          { autoClose: 10000 },
        );
        return;
      }

      if (dados.acao === 'VINCULADO') {
        toast.info(`Você recebeu acesso ao inbox ${nome}.`, { autoClose: 8000 });
      } else {
        toast.warning(`Seu acesso ao inbox ${nome} foi removido.`, { autoClose: 10000 });
      }
    },

    /**
     * Propaga o vínculo de bot de IA para a cópia do inbox que vive no `inboxMap`
     * do chatStore.
     *
     * Existem três cópias do inbox em memória: `authUser.inboxes` (mapa do token),
     * `authUser.inboxesFull` (lista da conta) e `chatStore.inboxMap` (carregada por
     * `_fetchAndPopulateInboxes`, com o status de conexão junto). Os eventos de
     * vínculo só atualizavam as duas primeiras.
     *
     * Isso importa porque `InboxModal` lê do `inboxMap` e o `PATCH /inboxes/:id`
     * manda o OBJETO INTEIRO: numa aba aberta desde antes de o Agente ser
     * vinculado, salvar qualquer campo (nome, fuso, horários) reenviava o
     * `idAgenteBotIa` congelado e desfazia o vínculo no servidor — sem ninguém
     * pedir, e sem nada na tela. O servidor agora ignora os campos ausentes, mas a
     * cópia local ainda precisa estar certa para a modal não MOSTRAR um vínculo que
     * já mudou.
     *
     * Lê de `inboxes`/`inboxesFull` (acabaram de ser atualizados pelo handler) em
     * vez do payload, para os dois eventos convergirem num caminho só.
     */
    atualizarVinculoBotIaNoChatStore(idInbox: IdInbox) {
      const fonte = this.inboxes[idInbox] ?? this.inboxesFull.find(i => i.id == idInbox);
      if (!fonte) return;
      try {
        const alvo = useChatStore().inboxMap.get(idInbox);
        if (!alvo) return;
        alvo.idInstanciaBotIa = fonte.idInstanciaBotIa;
        alvo.idAgenteBotIa = fonte.idAgenteBotIa;
      } catch (e) {
        // O chatStore pode não estar inicializado (evento chegando durante o boot).
        // Perder a propagação é inofensivo: o próximo `_fetchAndPopulateInboxes`
        // relê tudo do servidor.
        console.warn('[authUser] não foi possível propagar o vínculo de bot de IA ao chatStore', e);
      }
    },

    async onInboxPronto(inbox: InboxProntoPayload) {
      try {
        // Re-escopa o token para a conta do inbox recém-criado. `refreshToken`
        // apenas renovaria mantendo o `currentContaId` antigo — se ele diferisse
        // da conta do inbox, o novo token NÃO carregaria o inbox no mapa
        // `inboxes`, e toda chamada inbox-scoped daria 401 (e o retry pós-refresh
        // também). `switchConta` reemite os tokens já escopados à conta correta,
        // garantindo o inbox no token. Fallback para refreshToken se falhar.
        console.log('[onInboxPronto] 1');
        localStorage.setItem('selectedContaId', inbox.idConta);
        try {
          await this.service.switchConta(inbox.idConta);
        } catch {
          await this.service.refreshToken().catch(() => undefined);
        }
        console.log('[onInboxPronto] 2');
        await this.init(true);
        console.log('[onInboxPronto] 3');

        // Garante a conta correta selecionada (o inbox pertence a ela).
        const conta = this.contasFull.find(c => c.id == inbox.idConta);
        if (conta && this.currentConta?.id != conta.id) {
          this.currentConta = conta;
        }

        // Injeta o inbox diretamente (independe de o token já ter sido renovado),
        // garantindo que os getters currentAccountInboxes o enxerguem na hora.
        const inboxService = new InboxService();
        const full = await inboxService.getById(inbox.id);
        if (full) {
          this.inboxes[full.id] = full;
          if (!this.inboxesFull.find(i => i.id == full.id)) this.inboxesFull.push(full);
        }

        const chat = useChatStore();
        await chat._fetchAndPopulateInboxes([inbox.id]);

        // A inbox acabou de conectar: qualquer aviso de "desconectado" pendente
        // dela (semeado antes da conexão, ou por uma consulta de status que pegou
        // o token/caches ainda defasados) é obsoleto — some junto.
        useInboxAlertStore().notifyConnected(inbox.id);

        const ui = useUiStore();
        ui.selectedInboxId = inbox.id;
        ui.closeWhatsappSetupModal();

        // O socket entra nas salas dos inboxes a partir do wsToken avaliado no
        // (re)connect (server rejoin via evento 'conectar'). O token já foi
        // reescopado (switchConta) para a conta do novo inbox, então forçamos um
        // reconnect para o server re-entrar nas salas — incluindo a do inbox
        // recém-criado. Sem isso, mensagens ao novo inbox só chegam via WS após um
        // reload (connect() sozinho não basta: ele retorna cedo se já conectado).
        try {
          const ws = this.wsService;
          if (ws?.ws.connected) {
            ws.ws.disconnect();
            ws.ws.connect();
          } else {
            await ws?.connect();
          }
        } catch (e) {
          console.warn('[onInboxPronto] falha ao reconectar o websocket', e);
        }

        toast.success(`Inbox "${inbox.nome}" está pronto! 🎉`);
      } catch (e) {
        console.warn('[authUser] falha ao processar INBOX_PRONTO', e);
      } finally {
        // Marca a chegada do evento para quem está esperando por ele na tela (o
        // InboxModal mostra "finalizando a conexão" entre o vínculo e este
        // ponto). Vai no `finally` porque, mesmo se o reescopo de token falhar,
        // o backend já liberou a inbox — travar o spinner para sempre é pior.
        this.ultimoInboxPronto = { id: inbox.id, em: Date.now() };
      }
    },

    /**
     * DEBUG: dispara localmente o fluxo de INBOX_PRONTO sem depender de NATS/
     * websocket. Passe o idInbox recém-criado (dos logs do backend); se omitido,
     * usa o último inbox da conta atual. Exposto em `window.__forceInboxPronto`.
     */
    async debugForceInboxPronto(idInbox?: IdInbox) {
      const inboxService = new InboxService();
      let inbox: ResponseInbox | null = null;
      if (idInbox) {
        inbox = await inboxService.getById(idInbox);
      } else if (this.currentConta?.id) {
        // Sem id: busca fresco no server (o inbox recém-criado ainda não está no
        // estado local) e assume o mais recente da conta atual.
        const list = await inboxService.getByIdConta(this.currentConta.id);
        inbox = list.at(-1) ?? null;
      }
      if (!inbox) {
        console.warn('[authUser][debug] inbox não encontrada — informe o idInbox: window.__forceInboxPronto("<id>")');
        return;
      }
      console.log('[authUser][debug] forçando INBOX_PRONTO para', inbox.id);
      await this.onInboxPronto(inbox as unknown as InboxProntoPayload);
    },
    async _fetchInboxesFull() {
      this.inboxesFull = [];
      const inboxService = new InboxService();
      for (const inboxId of Object.keys(this.inboxes)) {
        const foundInbox = await inboxService.getById(inboxId);
        if (foundInbox && !this.inboxesFull.find(i => i.id == foundInbox.id)) this.inboxesFull.push(foundInbox)
      }
    },

    async _loadCanaisVisualizacaoGlobal() {
      try {
        const ids = await this.usuarioCanalService.getMeusCanaisVisualizacaoGlobal();
        this.canaisVisualizacaoGlobal = ids.reduce((acc, id) => { acc[id] = true; return acc; }, {} as Record<IdCanal, boolean>);
      } catch (e) {
        console.warn('[authUser] falha ao carregar canais com visualização global', e);
        this.canaisVisualizacaoGlobal = {};
      }
    },

    // Aplica nas stores locais as preferências (tema/som) vindas do backend via whoAmI.
    _aplicarPreferencias(idUsuario: IdUsuario, tema?: 'light' | 'dark', som?: SoundPreferences) {
      if (tema === 'light' || tema === 'dark') {
        // persist=false: apenas reflete o valor do servidor, sem re-gravar no backend.
        useUiStore().setTheme(tema, false);
      }
      if (som) {
        useSoundStore().updateSoundPreferences(idUsuario, new SoundPreferences(som));
      }
    },

    /**
     * Sonda o cookie de sessão em rota pública ('/') e, se ainda for válido,
     * hidrata a store como se o usuário tivesse acabado de logar.
     *
     * Necessário porque `init()` faz curto-circuito em '/' (não valida o cookie)
     * e a store não é persistida — então `isLoggedIn` nasce sempre `false`. O PWA
     * abre sempre no `start_url` '/', o que fazia a tela de login aparecer a cada
     * abertura mesmo com a sessão (cookie httpOnly, 12h) intacta.
     *
     * @returns `true` se a sessão foi restaurada e a store está pronta.
     */
    async tentarRestaurarSessao(): Promise<boolean> {
      try {
        // `init(true)` já checa a sessão antes de hidratar; chamar isLoggedIn
        // aqui seria uma segunda ida ao servidor pela mesma resposta.
        await this.init(true);
        return this.isLoggedIn;
      } catch (e) {
        // init() pode falhar DEPOIS de marcar isLoggedIn (ex.: usuário sem conta
        // vinculada). Sem o reset a store ficaria meio-inicializada na tela de
        // login e a guarda de /app quebraria na navegação seguinte.
        console.warn('[authUser] Falha ao restaurar sessão:', e);
        this._resetAuthState();
        return false;
      }
    },

    async init(revalidate: boolean = false, idConta?: IdConta) {
      console.log(`[Init] initialized=${this.initialized}, revalidate=${revalidate}`);

      if (this.initialized && !revalidate) return;

      if (window.location.pathname === '/' && !revalidate) {
        this.initialized = true;
        return;
      }

      this.isLoggedIn = await this.service.isLoggedIn();

      console.log(`[Init] IsLoggedIn?`, this.isLoggedIn)
      if (this.isLoggedIn) {
        const funData = await this.service.whoAmI();
        this.user = { id: funData.id, nome: funData.nome, email: funData.email, foto: funData.foto, hasGlobalAccess: funData.hasGlobalAccess, doisFatores: funData.doisFatores, notificacaoNavegador: funData.notificacaoNavegador };
        this.sessaoSimulada = funData.simulado ? { nomeAdmin: funData.nomeAdmin, expiraEm: funData.simulacaoExpiraEm } : null;
        this._aplicarPreferencias(funData.id, funData.tema, funData.som);
        this.canais = funData.canais;
        await this._loadCanaisVisualizacaoGlobal();
        this.contas = funData.contas;
        this.permissoes = funData.permissoes;
        this.inboxes = funData.inboxes;
        this.inboxesNaoConectadas = funData.inboxesNaoConectadas ?? [];
        this.inboxesVinculados = funData.inboxesVinculados ?? [];

        // Conta congelada (dataSuspensao preenchida) fica fora do seletor — o backend
        // já bloqueia o uso dela; com todas congeladas o whoAmI retorna 403 CONTA_CONGELADA.
        this.contasFull = (await this.usuarioService.getContasVinculadas(this.user.id)).filter(c => !c.dataSuspensao);
        if (this.contasFull.length == 0) throw new Error(`Este usuário não possui nenhuma conta vinculada.`);
        await this._fetchInboxesFull();
        const savedContaId = idConta ?? localStorage.getItem('selectedContaId');
        const contaIdToSelect = savedContaId && this.contasFull.find(c => c.id == makeIdConta(savedContaId))
          ? makeIdConta(savedContaId)
          : this.contasFull[0].id;

        // Seleciona conta SEM disparar reload de stores
        this.currentConta = this.contasFull.find(c => c.id == contaIdToSelect) ?? this.contasFull[0];

        localStorage.setItem('selectedContaId', contaIdToSelect);

        this.initialized = true;

        // Aguarda permissões da conta selecionada antes de deixar o app prosseguir
        const permissoesStore = usePermissoesStore();
        permissoesStore.initialized = false; // força recarregar se já estava inicializado
        await permissoesStore.init();
      } else {
        this.initialized = true;
      }

      return this;
    },

    async selectConta(idConta: IdConta, shouldReload: boolean = false) {
      const foundConta = this.contasFull.find((c) => c.id == idConta);
      if (!foundConta) throw new Error("Conta desconhecida.");

      await this.service.switchConta(idConta);

      this.currentConta = foundConta;
      localStorage.setItem('selectedContaId', idConta);

      // Aguarda permissões da nova conta antes de recarregar os stores
      await usePermissoesStore().recarregarMinhasPermissoes();

      if (shouldReload) {
        void this.reloadAllStoresForConta(idConta);
      }

      window.location.reload();
    },

    // 🆕 NOVO MÉTODO: Recarrega todos os stores
    reloadAllStoresForConta(idConta: IdConta) {
      void useTemplateStore().reloadForConta(idConta);
      void useQuadroKanbanStore().reloadForConta(idConta);
      void useInternalChatStore().reloadForConta();
      void useChatStore().reloadForConta();
      void useContatoStore().init(idConta, true);
    },

    async login(email: string, senha: string): Promise<true | 'requires2fa' | false> {
      try {
        const idConta = localStorage.getItem('selectedContaId') ?? undefined;
        const result = await this.service.login(email, senha, idConta) as { requires2fa?: boolean } | null;

        if (result && result.requires2fa) {
          this.requires2fa = true;
          return 'requires2fa';
        }

        this.requires2fa = false;
        await this.init(true);
        await useChatStore().init(true);
        return true;
      } catch (e) {
        console.warn(e);
        return false;
      }
    },

    async verify2fa(token: string): Promise<true | false> {
      try {
        await this.service.verify2fa(token);
        this.requires2fa = false;
        await this.init(true);
        await useChatStore().init(true);
        return true;
      } catch (e) {
        console.warn(e);
        return false;
      }
    },

    async resend2fa(): Promise<void> {
      await this.service.resend2fa();
    },

    _resetAuthState() {
      this.isLoggedIn = false;
      this.initialized = false;
      this.user = null;
      this.sessaoSimulada = null;
      this.canais = {};
      this.canaisVisualizacaoGlobal = {};
      this.contas = {};
      this.permissoes = {};
      this.inboxes = {};
      this.contasFull = [];
      this.inboxesFull = [];
      this.currentConta = null;
      usePermissoesStore().limpar();
      useInternalChatStore().limpar();
    },

    /**
     * Devolve `null` em caso de sucesso, ou a mensagem a mostrar (F-23: com o
     * motivo que o server deu, em vez de um "Falha ao simular login" genérico).
     */
    async simularLogin(idUsuario: IdUsuario, idConta?: IdConta): Promise<string | null> {
      try {
        await this.service.simularLogin(idUsuario, idConta);
        localStorage.removeItem('selectedContaId');
        this._resetAuthState();
        await this.init(true, idConta);
        await useChatStore().init(true);
        return null;
      } catch (e) {
        console.warn(e);
        return mensagemDeFalhaAoSimular(e);
      }
    },

    async loginWithToken(token: string) {
      try {
        await this.service.tokenLogin(token);
        await this.init(true);
        await useChatStore().init(true);
        return true;
      } catch (e) {
        console.warn(e);
        return false;
      }
    },

    async loginWithTokenFromEmbed(token: string, idConta: IdConta) {
      try {
        await this.service.tokenLogin(token);
        await this.init(false, idConta);
        return true;
      } catch (e) {
        console.warn(e);
        return false;
      }
    },

    async logout() {
      console.log("🔌 [Logout] Iniciando...");

      // ✅ Desconecta AMBOS os WebSockets
      await Promise.all([
        this.disconnectChatWebsocket(),
        this.disconnectInternalChatWebsocket()
      ]);

      await this.service.logout();
      useChatStore().chatContext = "customer";
      usePermissoesStore().limpar();
      useInternalChatStore().limpar();
      useUiStore().activeTab = "Lista";

      // ✅ Reseta TUDO
      this.isLoggedIn = false;
      this.initialized = false;
      this.user = null;
      this.sessaoSimulada = null;
      this.canais = {};
      this.canaisVisualizacaoGlobal = {};
      this.contas = {};
      this.permissoes = {};
      this.inboxes = {};
      this.contasFull = [];
      this.inboxesFull = [];
      this.currentConta = null;

      // ✅ Garante que os WebSockets estão null
      this.wsServiceChat = null;
      this.wsServiceInternal = null;

      console.log("✅ [Logout] Completo");
    },

    // ✅ Adiciona parâmetro force para forçar reconexão
    async connectInternalChatWebsocket(force: boolean = false) {
      console.log(`[ConnectWS] wsServiceInternal=${!!this.wsServiceInternal}, force=${force}`);

      // ✅ Se force=true, desconecta primeiro
      if (force && this.wsServiceInternal) {
        console.log("[ConnectWS] Forçando reconexão, desconectando primeiro...");
        await this.disconnectInternalChatWebsocket();
      }

      // ✅ Verifica se já existe (e não está forçando)
      if (this.wsServiceInternal && !force) {
        console.log("[ConnectWS] WebSocket interno já conectado");
        return this.wsServiceInternal;
      }

      try {
        if (!this.initialized) {
          console.warn("[ConnectWS] Tentando conectar antes de inicializar");
          return null;
        }

        console.log("🔌 [ConnectWS] Criando novo WebSocket...");
        this.wsServiceInternal = WebsocketService.getWSS(
          () => this.service.getWebsocketToken(),
          window.configs.SOCKET_URL_PROD as string
        );

        await this.wsServiceInternal.connect();

        console.log("✅ [ConnectWS] WebSocket conectado!");
        return this.wsServiceInternal;
      } catch (error) {
        console.error("❌ [ConnectWS] Erro:", error);
        return null;
      }
    },

    async disconnectChatWebsocket() {
      if (!this.wsServiceChat) {
        console.log("ℹ️ [DisconnectChat] Não há WebSocket do chat");
        return;
      }

      try {
        console.log("🔌 [DisconnectChat] Desconectando...");
        await this.wsServiceChat.disconnect();
        this.wsServiceChat = null;
        console.log("✅ [DisconnectChat] Desconectado");
      } catch (error) {
        console.error("❌ [DisconnectChat] Erro:", error);
        this.wsServiceChat = null; // ✅ Limpa mesmo com erro
      }
    },

    async disconnectInternalChatWebsocket() {
      if (!this.wsServiceInternal) {
        console.log("ℹ️ [DisconnectInternal] Não há WebSocket interno");
        return;
      }

      try {
        console.log("🔌 [DisconnectInternal] Desconectando...");
        await this.wsServiceInternal.disconnect();
        this.wsServiceInternal = null;
        console.log("✅ [DisconnectInternal] Desconectado");
      } catch (error) {
        console.error("❌ [DisconnectInternal] Erro:", error);
        this.wsServiceInternal = null; // ✅ Limpa mesmo com erro
      }
    },

    async getWebsocketToken() {
      if (!this.initialized) {
        console.warn("[GetToken] Chamado antes de inicializar");
        return null;
      }
      return await this.service.getWebsocketToken();
    },
    // ✅ NOVO: Método para atualizar dados do usuário (incluindo foto)
    async fetchUserProfile() {
      try {
        await FetchUtil.refresh();
        console.log('🔄 [FetchUserProfile] Recarregando dados do usuário...');

        // Chama o mesmo endpoint que init() usa (whoAmI)
        const funData = await this.service.whoAmI();

        // Atualiza APENAS o que mudou (foto, nome, etc)
        if (this.user) {
          this.user.id = funData.id;
          this.user.nome = funData.nome;
          this.user.email = funData.email;
          this.user.foto = funData.foto;
          this.user.hasGlobalAccess = funData.hasGlobalAccess;
          this.user.doisFatores = funData.doisFatores;
          this.user.notificacaoNavegador = funData.notificacaoNavegador;
          this._aplicarPreferencias(funData.id, funData.tema, funData.som);
        }

        console.log('✅ [FetchUserProfile] Dados recarregados:', this.user);
        return this.user;
      } catch (error) {
        console.error('❌ [FetchUserProfile] Erro:', error);
        throw error;
      }
    }
  },
})
